export interface WithRetryOptions {
  /** Max retries AFTER the first attempt. Default 3 → up to 4 total attempts. */
  maxRetries?: number;
  /** Base backoff in ms for exponential backoff. Default 500. */
  baseDelayMs?: number;
  /** Upper bound on any single backoff delay. Default 8000. */
  maxDelayMs?: number;
  /** Return true to retry this error; false (or exhausted retries) rethrows immediately. */
  isRetryable: (error: unknown) => boolean;
  /**
   * Optional: server-suggested delay for this error (e.g. Retry-After), in ms. When it returns a
   * finite number, it OVERRIDES the computed exponential backoff for that attempt. `attempt` is
   * the 0-based index of the attempt that just failed.
   */
  getRetryDelayMs?: (error: unknown, attempt: number) => number | undefined;
  /**
   * Aborts the BACKOFF, not the wrapped call — `fn` gets whatever signal its caller threaded into it
   * (typically the same one). Without this, a cancelled batch would sit out a full exponential
   * backoff before its next attempt's fetch rejected anyway: up to ~24s per in-flight call under
   * GRAPH_RETRY_OPTIONS' four attempts.
   */
  signal?: AbortSignal;
}

/**
 * Abort-aware delay: rejects the moment the signal aborts, rather than resolving late.
 *
 * Rejects with the signal's own `reason`, which for `AbortController#abort()` is a DOMException named
 * 'AbortError' — the exact rejection an aborted fetch produces. So a caller classifying errors needs
 * no separate case for "aborted while backing off" (see isRetryableGraphError, which returns false
 * for it either way).
 */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!signal) {
      setTimeout(resolve, ms);
      return;
    }
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal.reason);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Generic retry-with-backoff wrapper. Knows nothing about Graph/HTTP — retryability and any
 * server-suggested delay are supplied by the caller via predicates, so callers (e.g. the
 * SharePoint drive service) compose their own policy on top of this.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  opts: WithRetryOptions,
): Promise<T> {
  const {
    maxRetries = 3,
    baseDelayMs = 500,
    maxDelayMs = 8000,
    isRetryable,
    getRetryDelayMs,
    signal,
  } = opts;

  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt >= maxRetries || !isRetryable(error)) {
        throw error;
      }

      const suggested = getRetryDelayMs?.(error, attempt);
      const backoff = Math.min(maxDelayMs, baseDelayMs * 2 ** attempt);
      const delay = Number.isFinite(suggested)
        ? Math.min(maxDelayMs, suggested as number)
        : backoff;

      await sleep(delay, signal);
    }
  }
}
